import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { TransportPlannerProfileItemService } from './transport-planner-profile-item.service';
import {
  TransportPlannerProfileItemCreate,
  TransportPlannerProfileItemInitialState,
  TransportPlannerProfileItemLoad,
  TransportPlannerProfileItemUpdate,
} from './transport-planner-profile-item.actions';
import { TransportPlannerProfileModel } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportPlannerProfileItemStateModel extends TransportPlannerProfileModel {
  loaded: boolean;
}

const initialState: TransportPlannerProfileItemStateModel = {
  ...new TransportPlannerProfileModel(),
  loaded: false,
};

@State<TransportPlannerProfileItemStateModel>({
  name: 'transportPlannerProfileItem',
  defaults: initialState,
})
@Injectable()
export class TransportPlannerProfileItemState {
  constructor(
    private transportPlannerProfileItemService: TransportPlannerProfileItemService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getProfileId(state: TransportPlannerProfileItemStateModel): number {
    return state.id;
  }

  @Selector()
  static getState(state: TransportPlannerProfileItemStateModel): TransportPlannerProfileItemStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: TransportPlannerProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Action(TransportPlannerProfileItemLoad)
  getTransportPlannerProfileItem(
    ctx: StateContext<TransportPlannerProfileItemStateModel>,
    { payload }: TransportPlannerProfileItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportPlannerProfileItemService.getTransportPlannerProfile(payload).pipe(
      map((transportPlannerProfile: TransportPlannerProfileModel) => {
        ctx.patchState({
          ...new TransportPlannerProfileModel(transportPlannerProfile),
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportPlannerProfileItemCreate)
  createTransportPlannerProfileItem(
    ctx: StateContext<TransportPlannerProfileItemStateModel>,
    { payload }: TransportPlannerProfileItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportPlannerProfileItemService.createTransportPlannerProfile(payload.profile).pipe(
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.transportPlannerProfile.success.create');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportPlannerProfileItemUpdate)
  updateTransportPlannerProfileItem(
    ctx: StateContext<TransportPlannerProfileItemStateModel>,
    { payload }: TransportPlannerProfileItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportPlannerProfileItemService.updateTransportPlannerProfile(payload).pipe(
      map((updatedProfile: TransportPlannerProfileModel) => {
        ctx.patchState({ ...updatedProfile });
        this.notificationService.success('api.transportPlannerProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportPlannerProfileItemInitialState)
  setInitialState(ctx: StateContext<TransportPlannerProfileItemStateModel>): void {
    ctx.setState(initialState);
  }
}
